import React, { useRef, useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Box,
    Typography,
    Chip,
    Stack,
    useTheme,
    useMediaQuery,
    Slide,
    AppBar,
    Toolbar,
    Tooltip,
} from "@mui/material";
import {
    Close as CloseIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    GridOn as GridOnIcon,
    GridOff as GridOffIcon,
} from "@mui/icons-material";
import { TransitionProps } from "@mui/material/transitions";
import VideoObjectsTable from "../components/ObjectTable";
import { useVideoController } from "../hooks/useVideoController";
import { VideoPlayer } from "../components/VideoPlayer";
import { VideoWithDevice } from "../../../types/Video";
import "./VideoModalLayout.css";
import { NamedSite } from "../../../types/Site";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface VideoModalLayoutProps {
    readonly site: NamedSite;
    readonly video: VideoWithDevice;
    readonly open: boolean;
    readonly "data-testid"?: string;
    onClose: () => void;
}

const VideoModalLayout: React.FC<VideoModalLayoutProps> = ({
    site,
    open,
    video,
    "data-testid": dataTestId = "video-modal-layout",
    onClose,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);

    const {
        videoProps,
        tableProps,
        videoRef,
        videoMetadata,
        deviceConfig,
        displayBoundingBoxes,
        toggleDisplayBoundingBoxes,
    } = useVideoController({
        video,
        site,
    });

    const showTable = deviceConfig?.mode === "COUNTS_ENABLED";

    const videoSectionRef = useRef<HTMLDivElement>(null);
    const dialogPaperRef = useRef<HTMLDivElement>(null);
    const [videoElementHeight, setVideoElementHeight] = useState<number | null>(null);

    // Get video element when player is ready
    useEffect(() => {
        const checkVideoElement = () => {
            const element = videoRef.current?.getVideoElement();
            if (element) {
                setVideoElement(element);
            }
        };

        // Check immediately and set up interval
        checkVideoElement();
        const interval = setInterval(checkVideoElement, 100);

        return () => clearInterval(interval);
    }, []);

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const formatTimestamp = (timestamp: string | Date): string => {
        const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            timeZoneName: "short",
            hour12: false,
        };
        return Intl.DateTimeFormat(undefined, options).format(date);
    };

    const getModalDimensions = () => {
        if (isFullscreen) {
            return {
                width: "100vw",
                height: "100vh",
                maxWidth: "100vw",
                maxHeight: "100vh",
            };
        }

        if (isMobile) {
            return {
                width: "95vw",
                height: "90vh",
                maxWidth: "95vw",
                maxHeight: "90vh",
            };
        }

        return {
            width: "100%",
            maxWidth: "1600px",
            maxHeight: "90vh",
            minWidth: "900px",
        };
    };

    const getGridTemplateColumns = () => {
        if (!showTable) return "1fr";
        if (isMobile) return "1fr";
        if (videoMetadata?.isPortrait) {
            return "minmax(0, max-content) 1fr";
        }
        return "minmax(350px, 1fr) minmax(380px, 600px)";
    };

    const modalDimensions = getModalDimensions();

    const modalControls = ({ fullscreenIcon }: { fullscreenIcon?: React.ReactNode } = {}) => (
        <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
            {!isMobile && fullscreenIcon && (
                <Tooltip title="Toggle Fullscreen">
                    <IconButton
                        onClick={toggleFullscreen}
                        size="small"
                        aria-label="fullscreen-toggle"
                        sx={{
                            backgroundColor: "action.hover",
                            "&:hover": {
                                backgroundColor: "action.selected",
                            },
                        }}
                    >
                        {fullscreenIcon}
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title="Close">
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        backgroundColor: "action.hover",
                        "&:hover": {
                            backgroundColor: "error.light",
                            color: "error.main",
                        },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    );

    return (
        <>
            <Dialog
                data-testid={dataTestId}
                open={open}
                onClose={onClose}
                scroll="paper"
                slots={{ transition: Transition }}
                maxWidth={false}
                fullScreen={isFullscreen}
                slotProps={{
                    paper: {
                        ref: dialogPaperRef,
                        sx: {
                            ...modalDimensions,
                            margin: isFullscreen ? 0 : "auto",
                            borderRadius: isFullscreen ? 0 : 2,
                            display: "flex",
                            flexDirection: "column",
                        },
                    },
                }}
                sx={{
                    "& .MuiBackdrop-root": {
                        backgroundColor: isFullscreen ? "black" : "rgba(0, 0, 0, 0.8)",
                    },
                }}
            >
                {/* Header */}
                {isFullscreen ? (
                    <AppBar position="static" color="default" elevation={1}>
                        <Toolbar>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography
                                    variant="h4"
                                    color="text.primary"
                                    sx={{
                                        display: "block",
                                        mb: 0.5,
                                        opacity: 0.8,
                                    }}
                                >
                                    {site.displayName}
                                </Typography>
                                <Stack
                                    direction={isMobile ? "column" : "row"}
                                    spacing={isMobile ? 0.5 : 2}
                                    alignItems={isMobile ? "flex-start" : "center"}
                                    sx={{ mt: 0.5 }}
                                >
                                    <Chip
                                        size="small"
                                        label={video.device.displayName}
                                        variant="outlined"
                                        sx={{ fontSize: "0.75rem", height: 24 }}
                                    />
                                    <Chip
                                        size="small"
                                        label={formatTimestamp(video.time)}
                                        variant="outlined"
                                        sx={{ fontSize: "0.75rem", height: 24 }}
                                    />
                                </Stack>
                            </Box>
                            {modalControls({
                                fullscreenIcon: <FullscreenExitIcon />,
                            })}
                        </Toolbar>
                    </AppBar>
                ) : (
                    <DialogTitle
                        sx={{
                            p: 2,
                            backgroundColor: "grey.50",
                            borderBottom: 1,
                            borderColor: "divider",
                            flexShrink: 0,
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography
                                    variant="h4"
                                    color="text.primary"
                                    sx={{
                                        display: "block",
                                        mb: 0.5,
                                        opacity: 0.8,
                                    }}
                                >
                                    {site.displayName}
                                </Typography>
                                <Stack
                                    direction={isMobile ? "column" : "row"}
                                    spacing={isMobile ? 0.5 : 2}
                                    alignItems={isMobile ? "flex-start" : "center"}
                                    sx={{ mt: 0.5 }}
                                >
                                    <Chip
                                        size="small"
                                        label={video.device.displayName}
                                        variant="outlined"
                                        sx={{ fontSize: "0.75rem", height: 24 }}
                                    />
                                    <Chip
                                        size="small"
                                        label={formatTimestamp(video.time)}
                                        variant="outlined"
                                        sx={{ fontSize: "0.75rem", height: 24 }}
                                    />
                                </Stack>
                            </Box>
                            {modalControls({
                                fullscreenIcon: <FullscreenIcon />,
                            })}
                        </Stack>
                    </DialogTitle>
                )}

                {/* Content */}
                <DialogContent
                    dividers
                    className="video-modal-content-grid"
                    sx={{
                        p: 2,
                        backgroundColor: "grey.50",
                        flex: 1,
                        overflow: "hidden",
                        display: "grid",
                        gridTemplateColumns: getGridTemplateColumns(),
                        gridTemplateRows: isMobile ? "auto 1fr" : "1fr",
                        gap: 2,
                        maxHeight: isFullscreen ? "calc(100vh - 64px)" : undefined,
                        height: "100%",
                        minWidth: 0,
                        alignItems: "center",
                    }}
                >
                    {/* Video Section with Canvas Overlay */}
                    <Box
                        ref={videoSectionRef}
                        className={`video-section${videoMetadata?.isPortrait ? " portrait" : ""}`}
                        sx={{
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minWidth: 0,
                            minHeight: 0,
                            overflow: "hidden",
                            width: "100%",
                            height: "100%",
                            maxHeight: "100%",
                        }}
                    >
                        <VideoPlayer
                            ref={videoRef}
                            {...videoProps}
                            isFullscreen={isFullscreen}
                            onVideoElementResize={setVideoElementHeight}
                            className={`video-player-wrapper${videoMetadata?.isPortrait ? " portrait" : ""}`}
                        />
                    </Box>

                    {/* Table Section - Fill remaining space */}
                    {showTable && (
                        <Box
                            data-testid="object-table-section"
                            className="table-section"
                            sx={{
                                minWidth: 0,
                                width: "100%",
                                height: videoElementHeight ? `${videoElementHeight}px` : "100%",
                                maxHeight: videoElementHeight ? `${videoElementHeight}px` : "100%",
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                            }}
                        >
                            <VideoObjectsTable {...tableProps} />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default VideoModalLayout;
